import { StrictMode, Suspense, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { PublicClientApplication, EventType, type AccountInfo } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Brand fonts (Montserrat + IBM Plex Mono), then CoreUI Pro compiled with the Scryer
// brand. coreui.scss REPLACES @coreui/coreui-pro/dist/css/coreui.min.css - importing
// both ships CoreUI twice and leaves the app half-branded.
import "@scryerlabs/branding/fonts.css";
import "@scryerlabs/ui/coreui.scss";
import "./styles/custom.scss";

// i18n side-effect: register resources before the tree renders
import "./i18n/i18n";

import { msalConfig } from "./config/auth";
import { RequireAuthentication } from "./components/auth/RequireAuthentication";
import { AuthTokenInitializer } from "./components/auth/AuthTokenInitializer";
import { CustomerProvisionGate } from "./components/auth/CustomerProvisionGate";
import { LoadingSpinner } from "./components/common/LoadingSpinner";
import { FailureCard } from "./components/common/FailureCard";
import { AppErrorBoundary } from "./components/common/AppErrorBoundary";
import { accessTokenService } from "./services/access-token-service";
import { AppRoutes } from "./routes/routes";
import { ToastProvider } from "./context/ToastContext";
import { FailureProvider } from "./context/FailureContext";
import { ThemeProvider } from "@scryerlabs/ui";
import { initTelemetry, trackException } from "./lib/telemetry";

// Before MSAL, so a startup failure is reported too.
initTelemetry();

const queryClient = new QueryClient();

// The package default key, "scryer-theme", which the back office already uses. The FO was on
// "scryer-fo-theme"; moving to the shared key resets stored preferences once, which is fine with
// no live customers and leaves one key across the estate rather than one per app.
//
// defaultTheme="system" honours prefers-color-scheme on a first visit. Outside renderApp so the
// failure card is branded too — an unstyled error page is the one screen where looking broken is
// least affordable.
const render = (content: ReactNode) =>
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <ThemeProvider defaultTheme="system">{content}</ThemeProvider>
    </StrictMode>
  );

// Startup got far enough to know sign-in is impossible, so there is nothing to offer but a reload.
// Rendered without the router, MSAL or the query client, because reaching here means one of those
// is what failed.
const renderStartupFailure = (error: unknown) => {
  console.error("Portal failed to start", error);
  trackException(error, { stage: "startup" });
  render(
    <FailureCard
      title="We couldn't start the portal"
      message="Something went wrong before we could sign you in. Reloading usually clears it; if it doesn't, email help@scryerlabs.com."
      details={error instanceof Error ? error.message : null}
    />
  );
};

const renderApp = (msalInstance: PublicClientApplication) =>
  render(
    <BrowserRouter>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <FailureProvider>
              {/* Outside RequireAuthentication so a failure before sign-in is caught too. */}
              <AppErrorBoundary>
                <RequireAuthentication>
                  <AuthTokenInitializer>
                    <CustomerProvisionGate>
                      <Suspense fallback={<LoadingSpinner />}>
                        <AppRoutes />
                      </Suspense>
                    </CustomerProvisionGate>
                  </AuthTokenInitializer>
                </RequireAuthentication>
              </AppErrorBoundary>
            </FailureProvider>
          </ToastProvider>
        </QueryClientProvider>
      </MsalProvider>
    </BrowserRouter>
  );

async function bootstrap() {
  // Constructing throws on a malformed config — an empty VITE_ENTRA_* variable baked into the
  // build reaches the browser as a configuration error here, before any promise exists to reject.
  const msalInstance = new PublicClientApplication(msalConfig);
  await msalInstance.initialize();

  try {
    const response = await msalInstance.handleRedirectPromise();
    const account = response?.account ?? msalInstance.getAllAccounts()[0];
    if (account) {
      msalInstance.setActiveAccount(account);
    }
  } catch (error) {
    // A redirect we cannot complete is recoverable: leaving the active account unset drops the
    // member back to RequireAuthentication, which starts a fresh sign-in. Failing the whole
    // startup here would strand them on an error card for a stale URL they can simply retry.
    console.error("Could not complete the sign-in redirect", error);
    trackException(error, { stage: "sign-in redirect" });
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const account = (event.payload as { account?: AccountInfo }).account;
      if (account) {
        msalInstance.setActiveAccount(account);
      }
    }
  });

  // Initialize the token service synchronously, before any component renders, so the
  // CustomerProvisionGate's provision POST has a working token acquirer on first paint
  // (the AuthTokenInitializer effect would otherwise run too late).
  accessTokenService.initialize(msalInstance);

  renderApp(msalInstance);
}

bootstrap().catch(renderStartupFailure);
